import React, { useState, useEffect } from 'react';
import { Mail, Save, Send, Server, Lock, Bell, CheckCircle, XCircle, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { smtpAPI } from '../../services/api';
import { ResponsabiliWhistleblowingCard } from './ResponsabiliWhistleblowingCard';
import { RetentionSettingsCard } from './RetentionSettingsCard';
import { MaintenanceCard } from './MaintenanceCard';

const SettingsTab = () => {
  const [config, setConfig] = useState({
    enabled: false,
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Gmed Portal',
    use_tls: true,
    use_auth: true,  // NEW: authentication toggle
    notify_contacts: true,
    notify_dpo: true,
    notify_jobs: true,
    notify_whistleblowing: false,
    admin_email: '',
    // Whistleblowing specific emails
    odv_email: '',
    admin_email_whistleblowing: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await smtpAPI.get();
      setConfig(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error loading SMTP config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await smtpAPI.update(config);
      toast.success('Configurazione salvata con successo!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!config.admin_email) {
      toast.error('Inserisci prima un\'email admin per il test');
      return;
    }
    
    setTesting(true);
    try {
      await smtpAPI.test(config.admin_email);
      toast.success('Email di test inviata con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dell\'email di test');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="py-8 text-center">Caricamento...</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* SMTP Configuration Card */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Configurazione SMTP</CardTitle>
                <CardDescription>Configura il server email per le notifiche</CardDescription>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={handleTest} disabled={testing || !config.enabled}>
                <Send className="w-4 h-4 mr-2" />
                {testing ? 'Invio...' : 'Test Email'}
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-smtp-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div className="flex items-center space-x-3">
              {config.enabled ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-muted-foreground" />
              )}
              <div>
                <p className="font-medium">Sistema Email</p>
                <p className="text-sm text-muted-foreground">
                  {config.enabled ? 'Le notifiche email sono attive' : 'Le notifiche email sono disattivate'}
                </p>
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
              data-testid="smtp-enabled-switch"
            />
          </div>

          {/* SMTP Server Settings */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="host">Server SMTP</Label>
              <Input
                id="host"
                value={config.host}
                onChange={(e) => setConfig({ ...config, host: e.target.value })}
                placeholder="smtp.gmail.com"
                className="mt-2"
                data-testid="smtp-host-input"
              />
            </div>
            <div>
              <Label htmlFor="port">Porta</Label>
              <Input
                id="port"
                type="number"
                value={config.port}
                onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) })}
                placeholder="587"
                className="mt-2"
                data-testid="smtp-port-input"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={config.username}
                onChange={(e) => setConfig({ ...config, username: e.target.value })}
                placeholder="user@example.com"
                className="mt-2"
                disabled={!config.use_auth}
                data-testid="smtp-username-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  value={config.password}
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  placeholder="••••••••"
                  className="pl-10"
                  disabled={!config.use_auth}
                  data-testid="smtp-password-input"
                />
              </div>
            </div>
          </div>

          {/* Authentication Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <Label htmlFor="use_auth" className="font-medium">Autenticazione SMTP</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Disabilita per SMTP relay senza autenticazione (es. Google Workspace SMTP Relay)
              </p>
            </div>
            <Switch
              checked={config.use_auth}
              onCheckedChange={(checked) => setConfig({ ...config, use_auth: checked })}
              id="use_auth"
              data-testid="smtp-use-auth-toggle"
            />
          </div>

          {/* Google SMTP Relay Info */}
          {!config.use_auth && (
            <div className="flex items-start space-x-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-500">Configurazione Google SMTP Relay</p>
                <ul className="mt-2 space-y-1 text-muted-foreground">
                  <li>• Host: <code className="bg-muted px-1 rounded">smtp-relay.gmail.com</code></li>
                  <li>• Porta: <code className="bg-muted px-1 rounded">25</code> o <code className="bg-muted px-1 rounded">587</code></li>
                  <li>• TLS: Disabilitato per porta 25, abilitato per 587</li>
                  <li>• Richiede configurazione in Google Admin Console</li>
                </ul>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="from_email">Email Mittente</Label>
              <Input
                id="from_email"
                type="email"
                value={config.from_email}
                onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                placeholder="noreply@gmed.it"
                className="mt-2"
                data-testid="smtp-from-email-input"
              />
            </div>
            <div>
              <Label htmlFor="from_name">Nome Mittente</Label>
              <Input
                id="from_name"
                value={config.from_name}
                onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                placeholder="Gmed Portal"
                className="mt-2"
                data-testid="smtp-from-name-input"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Switch
              checked={config.use_tls}
              onCheckedChange={(checked) => setConfig({ ...config, use_tls: checked })}
              id="use_tls"
            />
            <Label htmlFor="use_tls">Usa TLS/STARTTLS</Label>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings Card */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Bell className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <CardTitle className="text-2xl">Impostazioni Notifiche</CardTitle>
              <CardDescription>Scegli quali notifiche ricevere via email</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="admin_email">Email Amministratore</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="admin_email"
                type="email"
                value={config.admin_email}
                onChange={(e) => setConfig({ ...config, admin_email: e.target.value })}
                placeholder="admin@gmed.it"
                className="pl-10"
                data-testid="admin-email-input"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Le notifiche verranno inviate a questo indirizzo
            </p>
          </div>

          <div className="space-y-3 pt-4">
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Messaggi di Contatto</p>
                  <p className="text-sm text-muted-foreground">Ricevi una notifica per ogni nuovo messaggio</p>
                </div>
              </div>
              <Switch
                checked={config.notify_contacts}
                onCheckedChange={(checked) => setConfig({ ...config, notify_contacts: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Lock className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium">Richieste al DPO</p>
                  <p className="text-sm text-muted-foreground">Ricevi una notifica per ogni richiesta privacy</p>
                </div>
              </div>
              <Switch
                checked={config.notify_dpo}
                onCheckedChange={(checked) => setConfig({ ...config, notify_dpo: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Candidature (Lavora con noi)</p>
                  <p className="text-sm text-muted-foreground">Ricevi una notifica per ogni nuova candidatura</p>
                </div>
              </div>
              <Switch
                checked={config.notify_jobs}
                onCheckedChange={(checked) => setConfig({ ...config, notify_jobs: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Segnalazioni Whistleblowing</p>
                  <p className="text-sm text-muted-foreground">Ricevi una notifica per nuove segnalazioni</p>
                </div>
              </div>
              <Switch
                checked={config.notify_whistleblowing}
                onCheckedChange={(checked) => setConfig({ ...config, notify_whistleblowing: checked })}
              />
            </div>

            {/* Whistleblowing Email Configuration */}
            {config.notify_whistleblowing && (
              <div className="ml-4 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20 space-y-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-500">Configurazione Email Whistleblowing</p>
                    <p className="text-muted-foreground mt-1">
                      Configura le email per il routing delle segnalazioni:
                    </p>
                    <ul className="mt-2 space-y-1 text-muted-foreground">
                      <li>• <strong>Email OdV:</strong> riceve tutte le segnalazioni tranne "Segnalazioni contro OdV"</li>
                      <li>• <strong>Email Amministratore WB:</strong> riceve le segnalazioni "contro OdV"</li>
                    </ul>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="odv_email">Email OdV (Organismo di Vigilanza)</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="odv_email"
                        type="email"
                        value={config.odv_email}
                        onChange={(e) => setConfig({ ...config, odv_email: e.target.value })}
                        placeholder="odv@gmed.it"
                        className="pl-10"
                        data-testid="odv-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="admin_email_whistleblowing">Email Amministratore (segnalazioni contro OdV)</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="admin_email_whistleblowing"
                        type="email"
                        value={config.admin_email_whistleblowing}
                        onChange={(e) => setConfig({ ...config, admin_email_whistleblowing: e.target.value })}
                        placeholder="admin@gmed.it"
                        className="pl-10"
                        data-testid="admin-email-whistleblowing-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* OdV / Custodi Whistleblowing */}
      <ResponsabiliWhistleblowingCard />

      {/* Conservazione dei dati */}
      <RetentionSettingsCard />

      {/* Messa in produzione */}
      <MaintenanceCard />

      {/* Help Card */}
      <Card className="glass-card bg-muted/50">
        <CardContent className="pt-6">
          <h4 className="font-medium mb-3">Configurazione per provider comuni:</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 rounded-lg bg-background">
              <p className="font-medium text-primary">Gmail</p>
              <p className="text-muted-foreground">smtp.gmail.com : 587</p>
              <p className="text-xs text-muted-foreground mt-1">Richiede App Password</p>
            </div>
            <div className="p-3 rounded-lg bg-background">
              <p className="font-medium text-primary">Outlook/Office 365</p>
              <p className="text-muted-foreground">smtp.office365.com : 587</p>
            </div>
            <div className="p-3 rounded-lg bg-background">
              <p className="font-medium text-primary">SendGrid</p>
              <p className="text-muted-foreground">smtp.sendgrid.net : 587</p>
              <p className="text-xs text-muted-foreground mt-1">Username: apikey</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsTab;
